import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { ViewIcon } from "./icons";
import { PrivateCollectionItem } from "./PrivateCollectionItem";

export interface PairingItemSuccessProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product image URL
   */
  productImage: string;
  /**
   * Brand name (e.g., "ANYW3AR")
   */
  brandName: string;
  /**
   * Product name (e.g., "Pigalle Denim Jacket - Black")
   */
  productName: string;
  /**
   * NFC chip ID
   */
  nfcId: string;
  /**
   * Product edition number
   */
  productNumber: {
    current: number;
    total: number;
  };
  /**
   * Product size (e.g., "XL")
   */
  productSize: string;
  /**
   * Callback when unpair button is clicked
   */
  onUnpair: () => void;
  /**
   * Callback when "Go to my collection" button is clicked
   */
  onGoToCollection: () => void;
}

/**
 * Pairing Item Success component
 *
 * Displays success message after pairing an NFC product with the user's account.
 * Shows product details and provides navigation to the user's collection.
 *
 * This component appears after the PairingItemForm is successfully submitted.
 *
 * @example
 * ```tsx
 * <PairingItemSuccess
 *   productImage="/images/jacket.jpg"
 *   brandName="ANYW3AR"
 *   productName="Pigalle Denim Jacket - Black"
 *   nfcId="adnoa46z5vr1z35ef"
 *   productNumber={{ current: 24, total: 333 }}
 *   productSize="XL"
 *   onUnpair={() => handleUnpair()}
 *   onGoToCollection={() => navigate('/collection')}
 * />
 * ```
 */
export const PairingItemSuccess = React.forwardRef<
  HTMLDivElement,
  PairingItemSuccessProps
>(
  (
    {
      className,
      productImage,
      brandName,
      productName,
      nfcId,
      productNumber,
      productSize,
      onUnpair,
      onGoToCollection,
      ...props
    },
    ref
  ) => {
    const successMessage = "Congrats! Your item was successfully added to your collection.";
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-[2rem] items-start w-full box-border",
          className
        )}
        {...props}
      >
        {/* Header */}
        <div className="flex gap-[1rem] items-center shrink-0 w-full">
          <p className="font-montserrat font-bold leading-normal shrink-0 text-[0.9375rem] text-black text-center uppercase">
            Pairing NFC XP
          </p>
        </div>

        {/* Success Message */}
        <div className="flex items-center justify-between shrink-0 w-full">
          <div className="flex flex-1 flex-col font-montserrat font-normal justify-end leading-[0] min-h-px min-w-px shrink-0 text-[0.9375rem] text-gray-500">
            <p className="leading-[1.25rem] whitespace-pre-wrap">
              {successMessage}
            </p>
          </div>
        </div>

        {/* Product Details (reuses PrivateCollectionItem) */}
        <div className="flex flex-col items-start shrink-0 w-full">
          <PrivateCollectionItem
            productImage={productImage}
            brandName={brandName}
            productName={productName}
            nfcId={nfcId}
            productNumber={productNumber}
            productSize={productSize}
            onUnpair={onUnpair}
          />
        </div>

        {/* Go to Collection Button */}
        <Button
          variant="primary-dark"
          size="primary-dark"
          onClick={onGoToCollection}
          leftIcon={<ViewIcon size={12} />}
          className="h-[2.5rem] w-full rounded-[0.375rem]"
        >
          Go to my collection
        </Button>
      </div>
    );
  }
);

PairingItemSuccess.displayName = "PairingItemSuccess";
